import { useEventTypeStore } from '../context/EventTypeContext';
import { EventType } from '../types/ViewModel/EventType';
import { TankersEvent } from '../types/ViewModel/TankersEvent';
import { EventTypeDataVersion } from '../types/ViewModel/EventTypeDataVersion';
import { EventTypeGroup } from '../types/ViewModel/EventTypeGroup';
import EventTypeFactory from '../factory/EventTypeFactory';
import EventTypeGroupFactory from '../factory/EventTypeGroupFactory';
import { EventTypeData } from '../types/ViewModel/EventTypeData';
import { RotationTreeEnum } from '../enums/rotationTreeEnum';
import { ReportLocationEnum } from '../enums/reportLocationEnum';
import { MoveEventTypeMapBM } from '../types/BusinessModel/MoveEventTypeMapBM';
import MoveEventTypeMapFactory from '../factory/MoveEventTypeMapFactory';
import { IRotationNode } from '../types/ViewModel/IRotationNode';
import useRotation from './useRotation';

export default () => {
    const { eventTypeArray } = useEventTypeStore();
    const { createUnknownEvent } = EventTypeFactory();
    const { createUnknownEventTypeGroup } = EventTypeGroupFactory();
    const { createMoveEventTypeMapItemBM } = MoveEventTypeMapFactory();
    const { getImosPortFunctionFromNode } = useRotation();

    const getEventType = (eventTypeId: number | null, level: RotationTreeEnum | null): EventType | null => {
        return (
            eventTypeArray.find(
                (et) => et.eventTypeId === eventTypeId && (!level || et.eventTypeGroup?.eventTypeLevel?.eventTypeLevelId === level)
            ) ?? null
        );
    };

    const getNodeTypeFromEvent = (ev: TankersEvent): RotationTreeEnum => {
        if (ev.eventTypeContextId === ReportLocationEnum.Sea) return RotationTreeEnum.Sea;
        if (ev.cargoOperationId) return RotationTreeEnum.CargoOperation;
        if (ev.berthCallId) return RotationTreeEnum.BerthCall;
        return RotationTreeEnum.PortCall;
    };

    const getEventTypeFromEvent = (event: TankersEvent): EventType | null => {
        let eventType = getEventType(event.eventTypeId, event.eventLevel);
        if (!eventType) {
            const eventTypeGroup = createUnknownEventTypeGroup();
            eventType = createUnknownEvent(event, eventTypeGroup);
        }
        return eventType ?? null;
    };

    const getEventTypeGroup = (eventTypeData: EventTypeData | null, evTypeGroupId: number, level: RotationTreeEnum | null): EventTypeGroup | null => {
        if (eventTypeData && eventTypeData.eventTypeLevelArray && eventTypeData.eventTypeLevelArray.length > 0) {
            const eventTypeGroupArray = eventTypeData.eventTypeLevelArray.flatMap((o) => o.eventTypeGroupArray);
            return (
                eventTypeGroupArray?.find(
                    (etg) => etg.eventTypeGroupId === evTypeGroupId && (!level || etg.eventTypeLevel?.eventTypeLevelId === level)
                ) ?? null
            );
        }
        return null;
    };

    const getEventTypeDataVersion = (eventTypeData: EventTypeData | null): EventTypeDataVersion | null => {
        return eventTypeData?.eventTypeDataVersion ?? null;
    };

    const getMoveEventTypeMapList = (eventTypeData: EventTypeData | null): MoveEventTypeMapBM[] => {
        const evTypeMap: MoveEventTypeMapBM[] = [];
        if (eventTypeData) {
            const evTypeGrList = eventTypeData.eventTypeLevelArray.flatMap((o) => o.eventTypeGroupArray);
            evTypeGrList.forEach((evTypeGr) => {
                evTypeGr.requiredConditionalMandatoryArray?.forEach((reqCondMand) => {
                    const evTypeGrReqFrom = getEventTypeGroup(eventTypeData, reqCondMand.eventTypeGroupId, null);
                    if (evTypeGrReqFrom) {
                        evTypeGrReqFrom.eventTypeArray.forEach((evTypeFrom) => {
                            const evTypeReq = getEventType(reqCondMand.requiredPreviousTypeId, null);
                            if (evTypeReq) {
                                const evTypeGrReqTo = getEventTypeGroup(eventTypeData, evTypeReq.eventTypeGroupId, null);
                                const toMapList: number[] = [];
                                if (evTypeGrReqTo) {
                                    evTypeGrReqTo.eventTypeArray.forEach((evTypeTo) => {
                                        toMapList.push(evTypeTo.eventTypeId);
                                    });
                                }
                                if (toMapList.length > 0) {
                                    const evTypeMapItem = evTypeMap.find((o) => o.fromEventTypeId === evTypeFrom.eventTypeId);
                                    if (evTypeMapItem) {
                                        evTypeMapItem.toEventTypeIdList = evTypeMapItem.toEventTypeIdList.concat(toMapList);
                                    } else {
                                        evTypeMap.push(createMoveEventTypeMapItemBM(evTypeFrom.eventTypeId, toMapList));
                                    }
                                }
                            }
                        });
                    }
                });
            });
        }
        return evTypeMap;
    };

    const filterEventTypeGroup = (evTypeGrList: EventTypeGroup[], selectedNode: IRotationNode): EventTypeGroup[] => {
        const imosPortFunc = getImosPortFunctionFromNode(selectedNode);
        if (imosPortFunc) {
            const evTypeGrListFiltered = evTypeGrList.filter((evType) => {
                // If no map items for this event type group, include this event type group
                if (!evType.imosPortFunctionMapList || evType.imosPortFunctionMapList.length === 0) return true;
                // If map item exists for event type group for relevant imos port function, include this event type group
                if (evType.imosPortFunctionMapList.find((o) => o === imosPortFunc)) return true;
                // Else, do not include event type group
                return false;
            });
            return evTypeGrListFiltered;
        }
        return evTypeGrList;
    };

    return {
        getEventType,
        getEventTypeFromEvent,
        getEventTypeGroup,
        getEventTypeDataVersion,
        getNodeTypeFromEvent,
        getMoveEventTypeMapList,
        filterEventTypeGroup,
    };
};
